import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { useApi } from '../../../../hooks/useApi';
import usePersistedState from '../../../../hooks/usePersistedState';
import * as XLSX from 'xlsx';
import { fmtDateTime } from '../../../../utils/ruDates';

const TURNOVER_LABELS = {
    UNSPECIFIED: 'Не определено',
    TURNOVER_GRADE_NONE: 'Нет статуса',
    DEFICIT: 'Дефицит',
    POPULAR: 'Очень популярный',
    ACTUAL: 'Популярный',
    SURPLUS: 'Избыток',
    NO_SALES: 'Без продаж',
    WAS_NO_SALES: 'Был без продаж',
    RESTRICTED_NO_SALES: 'Без продаж, ограничен',
    COLLECTING_DATA: 'Сбор данных',
    WAITING_FOR_SUPPLY: 'Ожидает поставки',
    WAS_DEFICIT: 'Был дефицитным',
    WAS_POPULAR: 'Был очень популярным',
    WAS_ACTUAL: 'Был популярным',
    WAS_SURPLUS: 'Был избыточным',
};

const csvColumns = [
    ['offer_id', 'Артикул'],
    ['sku', 'SKU'],
    ['name', 'Название'],
    ['cluster_name', 'Кластер'],
    ['cluster_id', 'ID кластера'],
    ['macrolocal_cluster_id', 'ID макролокального кластера'],
    ['warehouse_name', 'Склад'],
    ['warehouse_id', 'ID склада'],
    ['available', 'Доступно к продаже'],
    ['valid_stock_count', 'Готовим к продаже'],
    ['requested_stock_count', 'В заявках на поставку'],
    ['transit_stock_count', 'В поставках в пути'],
    ['other_stock_count', 'На проверке'],
    ['return_from_customer_stock_count', 'Возврат от покупателей'],
    ['return_to_seller_stock_count', 'Готовится к вывозу'],
    ['stock_defect_stock_count', 'Брак со стока'],
    ['transit_defect_stock_count', 'Брак с поставки'],
    ['excess_stock_count', 'Излишки'],
    ['expiring_stock_count', 'Истекает срок'],
    ['waiting_docs_stock_count', 'Ожидает документов'],
    ['ads', 'Продаж в день за 28 дней'],
    ['ads_cluster', 'Продаж в день за 28 дней в кластере'],
    ['idc', 'Дней запаса'],
    ['idc_cluster', 'Дней запаса в кластере'],
    ['days_without_sales', 'Дней без продаж'],
    ['days_without_sales_cluster', 'Дней без продаж в кластере'],
    ['turnover_grade', 'Ликвидность'],
    ['turnover_grade_cluster', 'Ликвидность в кластере'],
    ['item_tags', 'Теги'],
    ['synced_at', 'Синхронизация'],
];

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString('ru-RU');
};

const todayIso = () => {
    const date = new Date();
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const daysAgoIso = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const toNumber = (value) => Number(value || 0);

const formatMetric = (value, digits = 2) => {
    if (value === null || value === undefined || value === '') return '-';
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    if (Number.isInteger(number)) return String(number);
    return number.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });
};

const getTurnoverLabel = (value) => {
    if (!value) return '-';
    return TURNOVER_LABELS[value] || value;
};

const getRowValue = (row, key) => {
    if (key === 'available') {
        return row.available ?? row.available_stock_count ?? 0;
    }
    if (key === 'cluster_name') {
        return row.cluster_name || row.cluster_id || '';
    }
    if (key === 'warehouse_name') {
        return row.warehouse_name || row.warehouse_id || '';
    }
    if (key === 'item_tags') {
        return Array.isArray(row.item_tags) ? row.item_tags.join(', ') : '';
    }
    return row[key];
};

const csvEscape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (rows, filename) => {
    const header = csvColumns.map(([, label]) => csvEscape(label)).join(';');
    const lines = rows.map((row) => csvColumns.map(([key]) => {
        const value = key === 'synced_at' ? formatDateTime(row.synced_at) : getRowValue(row, key);
        return csvEscape(value);
    }).join(';'));
    const blob = new Blob([`\uFEFF${[header, ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const replenishmentExportColumns = [
    ['article', 'Артикул'],
    ['sku', 'SKU'],
    ['name', 'Название'],
    ['cluster_name', 'Кластер'],
    ['warehouse_name', 'Склад'],
    ['current_available', 'Доступно'],
    ['in_motion', 'В движении'],
    ['avg_demand_per_day', 'Спрос/день'],
    ['stock_cover_days', 'Дней запаса'],
    ['horizon_days', 'Горизонт'],
    ['box_multiple', 'Кратность'],
    ['raw_need', 'Потребность'],
    ['uncapped_units', 'Без лимита остатка'],
    ['distributable', 'Распределяемый остаток'],
    ['recommended_units', 'Рекомендовано'],
    ['reason', 'Причина'],
];

const boxRuleColumns = [
    ['article', 'Артикул'],
    ['multiple', 'Кратность'],
    ['distributable', 'Распределяемый остаток'],
];

const clusterShareColumns = [
    ['cluster_name', 'Кластер'],
    // Старое название колонки принимаем при загрузке файлов, скачанных раньше.
    ['share', 'Доля, %', ['% распределения']],
];

// Пустая строка — «не задано», иначе число; запятую принимаем как разделитель.
const toOptionalNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const number = Number(String(value).replace(',', '.').replace(/\s/g, ''));
    return Number.isFinite(number) ? number : null;
};

// Ответ suggested-shares → штуки по кластерам и итог за период.
const toShareStats = (suggested) => {
    const items = Array.isArray(suggested?.items) ? suggested.items : [];
    const units = {};
    items.forEach((item) => { units[item.cluster_name] = Number(item.demand_units || 0); });
    return {
        periodFrom: suggested?.period_from || null,
        periodTo: suggested?.period_to || null,
        units,
        total: Object.values(units).reduce((sum, value) => sum + value, 0),
    };
};

const formatShareUnits = (stats, clusterName) => {
    const units = stats.units[clusterName] || 0;
    if (!units) return '0 шт';
    const percent = stats.total > 0 ? (units * 100) / stats.total : 0;
    return `${units.toLocaleString('ru-RU')} шт · ${formatMetric(percent, 1)} %`;
};

// API отклоняет отрицательные значения (422) — тогда настройки молча не сохранятся.
const distributableValue = (value) => {
    const number = toOptionalNumber(value);
    return number === null || number < 0 ? null : Math.trunc(number);
};

const shareValue = (value) => {
    const number = toOptionalNumber(value);
    return number === null || number < 0 ? null : number;
};

const downloadSettingsSheet = (rows, columns, numericKeys, sheetName, filename) => {
    const prepared = rows.map((row) => {
        const out = { ...row };
        numericKeys.forEach((key) => { out[key] = toOptionalNumber(row[key]); });
        return out;
    });
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheetFromRows(prepared, columns), sheetName);
    XLSX.writeFile(book, filename);
};

// Первый лист файла → строки по заголовкам. Принимаем и русские заголовки из
// нашей выгрузки, и служебные ключи (article, multiple, ...).
const readSettingsSheet = async (file, columns) => {
    const book = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = book.Sheets[book.SheetNames[0]];
    const raw = sheet ? XLSX.utils.sheet_to_json(sheet, { defval: '' }) : [];
    return raw.map((row) => {
        const normalized = {};
        Object.entries(row).forEach(([header, value]) => {
            normalized[String(header).trim().toLowerCase()] = value;
        });
        const out = {};
        columns.forEach(([key, label, aliases = []]) => {
            const value = [label, ...aliases, key]
                .map((name) => normalized[name.toLowerCase()])
                .find((candidate) => candidate !== undefined) ?? '';
            out[key] = value === null || value === undefined ? '' : String(value).trim();
        });
        return out;
    });
};

const MODE_LABELS = {
    article_cluster: 'Артикул - Кластер',
    article_warehouse: 'Артикул - Склад',
};

const sheetFromRows = (rows, columns) => XLSX.utils.aoa_to_sheet([
    columns.map(([, label]) => label),
    ...rows.map((row) => columns.map(([key]) => {
        const value = row[key];
        return value === null || value === undefined ? '' : value;
    })),
]);

/**
 * Итоговая книга по подсорту: сам расчёт + разрезы по кластеру и по артикулу +
 * входные данные (кратность, доли регионов) и параметры расчёта, чтобы файл
 * можно было отдать на закупку как есть.
 */
const downloadReplenishmentWorkbook = ({ rows, boxRules, clusterDistribution, params, meta, filename }) => {
    const book = XLSX.utils.book_new();

    const exportColumns = (meta?.mode || params.mode) === 'article_warehouse'
        ? replenishmentExportColumns
        : replenishmentExportColumns.filter(([key]) => key !== 'warehouse_name');
    XLSX.utils.book_append_sheet(book, sheetFromRows(rows, exportColumns), 'Подсорт');

    const byCluster = new Map();
    const byArticle = new Map();
    rows.forEach((row) => {
        const clusterKey = row.cluster_name || 'Без кластера';
        const cluster = byCluster.get(clusterKey) || { cluster_name: clusterKey, positions: 0, recommended_units: 0 };
        cluster.positions += 1;
        cluster.recommended_units += Number(row.recommended_units || 0);
        byCluster.set(clusterKey, cluster);

        const articleKey = row.article || '';
        const article = byArticle.get(articleKey)
            || { article: articleKey, name: row.name || '', clusters: 0, recommended_units: 0 };
        article.clusters += 1;
        article.recommended_units += Number(row.recommended_units || 0);
        byArticle.set(articleKey, article);
    });

    XLSX.utils.book_append_sheet(book, sheetFromRows(
        Array.from(byCluster.values()).sort((a, b) => b.recommended_units - a.recommended_units),
        [['cluster_name', 'Кластер'], ['positions', 'Позиций'], ['recommended_units', 'К отгрузке, шт']],
    ), 'По кластерам');

    XLSX.utils.book_append_sheet(book, sheetFromRows(
        Array.from(byArticle.values()).sort((a, b) => b.recommended_units - a.recommended_units),
        [['article', 'Артикул'], ['name', 'Название'], ['clusters', 'Кластеров'], ['recommended_units', 'К отгрузке, шт']],
    ), 'По артикулам');

    XLSX.utils.book_append_sheet(book, sheetFromRows(
        boxRules.filter((row) => row.article).map((row) => ({
            ...row,
            multiple: toOptionalNumber(row.multiple),
            distributable: toOptionalNumber(row.distributable),
        })),
        boxRuleColumns,
    ), 'Кратность коробов');

    XLSX.utils.book_append_sheet(book, sheetFromRows(
        clusterDistribution.filter((row) => row.cluster_name).map((row) => ({
            ...row,
            share: toOptionalNumber(row.share),
        })),
        clusterShareColumns,
    ), 'Доли регионов');

    XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet([
        ['Параметр', 'Значение'],
        ['Режим', MODE_LABELS[params.mode] || params.mode],
        ['Период с', meta?.period_from || params.period_from || ''],
        ['Период по', meta?.period_to || params.period_to || ''],
        ['Горизонт подсорта, дней', params.horizon_days],
        ['Минимум дней наблюдения', params.min_obs_days_for_rate],
        ['Строк в расчёте', rows.length],
        ['Сформировано', fmtDateTime()],
    ]), 'Параметры');

    XLSX.writeFile(book, filename);
};

const emptyFilters = {
    query: '',
    cluster: '',
    warehouse: '',
    turnover: '',
    onlyWithStock: false,
};

const inventoryColumns = [
    { key: 'rowNum', header: '№', width: 56, type: 'number', sticky: true, sortable: false, resizable: false },
    { key: 'offer_id', header: 'Артикул', width: 150, type: 'text', sticky: true },
    { key: 'sku', header: 'SKU', width: 120, type: 'text' },
    { key: 'name', header: 'Название', width: 280, type: 'text' },
    { key: 'cluster_name', header: 'Кластер', width: 170, type: 'text' },
    { key: 'warehouse_name', header: 'Склад', width: 210, type: 'text' },
    {
        key: 'available',
        header: 'Доступно',
        width: 110,
        type: 'number',
        align: 'right',
        render: (value, row) => formatMetric(row.available ?? row.available_stock_count, 0),
    },
    { key: 'valid_stock_count', header: 'Готовим', width: 105, type: 'number', align: 'right', render: (value) => formatMetric(value, 0) },
    { key: 'requested_stock_count', header: 'В заявках', width: 110, type: 'number', align: 'right', render: (value) => formatMetric(value, 0) },
    { key: 'transit_stock_count', header: 'В пути', width: 95, type: 'number', align: 'right', render: (value) => formatMetric(value, 0) },
    {
        key: 'ads',
        header: 'Прод./день',
        width: 115,
        type: 'number',
        align: 'right',
        render: (value) => formatMetric(value, 2),
    },
    {
        key: 'idc',
        header: 'Дней запаса',
        width: 120,
        type: 'number',
        align: 'right',
        render: (value) => formatMetric(value, 1),
    },
    {
        key: 'turnover_grade_display',
        header: 'Ликвидность',
        width: 160,
        type: 'text',
    },
    {
        key: 'synced_at',
        header: 'Синхронизация',
        width: 160,
        type: 'date',
        format: formatDateTime,
    },
];

const replenishmentColumns = [
    { key: 'rowNum', header: '№', width: 56, type: 'number', sticky: true, sortable: false, resizable: false },
    { key: 'article', header: 'Артикул', width: 150, type: 'text', sticky: true },
    { key: 'sku', header: 'SKU', width: 120, type: 'text' },
    { key: 'name', header: 'Название', width: 280, type: 'text' },
    { key: 'cluster_name', header: 'Кластер', width: 170, type: 'text' },
    { key: 'warehouse_name', header: 'Склад', width: 210, type: 'text' },
    { key: 'current_available', header: 'Доступно', width: 110, type: 'number', align: 'right', render: (value) => formatMetric(value, 0) },
    { key: 'in_motion', header: 'В движении', width: 115, type: 'number', align: 'right', render: (value) => formatMetric(value, 0) },
    { key: 'avg_demand_per_day', header: 'Спрос/день', width: 120, type: 'number', align: 'right', render: (value) => formatMetric(value, 2) },
    { key: 'stock_cover_days', header: 'Дней запаса', width: 120, type: 'number', align: 'right', render: (value) => formatMetric(value, 1) },
    { key: 'box_multiple', header: 'Кратность', width: 105, type: 'number', align: 'right', render: (value) => value || '-' },
    { key: 'raw_need', header: 'Потребность', width: 120, type: 'number', align: 'right', render: (value) => formatMetric(value, 1) },
    {
        key: 'uncapped_units',
        header: 'Без лимита',
        width: 110,
        type: 'number',
        align: 'right',
        render: (value, row) => (Number(value) !== Number(row.recommended_units) ? formatMetric(value, 0) : '-'),
    },
    {
        key: 'recommended_units',
        header: 'Рекомендовано',
        width: 135,
        type: 'number',
        align: 'right',
        render: (value) => <strong>{formatMetric(value, 0)}</strong>,
    },
    { key: 'reason', header: 'Причина', width: 260, type: 'text' },
];

// Причина без чисел — для фильтра: «урезано … (120 шт на артикул)» → одна группа.
const reasonGroup = (reason) => String(reason || '-').split(';')[0].replace(/\s*\(.*\)\s*$/, '').trim() || '-';

const emptyReplenishmentFilters = {
    cluster: '',
    warehouse: '',
    reason: '',
    onlyToShip: false,
};

function SummaryLine({ rows, totalRows }) {
    const totals = useMemo(() => {
        return rows.reduce((acc, item) => {
            acc.available += toNumber(item.available ?? item.available_stock_count);
            acc.valid += toNumber(item.valid_stock_count);
            acc.requested += toNumber(item.requested_stock_count);
            acc.transit += toNumber(item.transit_stock_count);
            return acc;
        }, { available: 0, valid: 0, requested: 0, transit: 0 });
    }, [rows]);

    return (
        <div className="muted">
            Строк: {rows.length}{totalRows ? ` из ${totalRows}` : ''} · Доступно: {totals.available} · Готовим: {totals.valid} · В заявках: {totals.requested} · В пути: {totals.transit}
        </div>
    );
}

function buildOptions(rows, key) {
    return Array.from(new Set(rows.map((row) => String(getRowValue(row, key) || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ru'));
}

function filterRows(rows, filters) {
    const query = filters.query.trim().toLowerCase();
    return rows.filter((row) => {
        if (query) {
            const haystack = [
                row.offer_id,
                row.sku,
                row.name,
                row.cluster_name,
                row.cluster_id,
                row.warehouse_name,
                row.warehouse_id,
            ].join(' ').toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        if (filters.cluster && String(getRowValue(row, 'cluster_name')) !== filters.cluster) return false;
        if (filters.warehouse && String(getRowValue(row, 'warehouse_name')) !== filters.warehouse) return false;
        if (filters.turnover) {
            const turnover = row.turnover_grade_cluster || row.turnover_grade || '';
            if (turnover !== filters.turnover) return false;
        }
        if (filters.onlyWithStock && toNumber(row.available ?? row.available_stock_count) <= 0) return false;
        return true;
    });
}

function FilterBar({ rows, filters, onChange, onReset, onExport, exportDisabled }) {
    const clusters = useMemo(() => buildOptions(rows, 'cluster_name'), [rows]);
    const warehouses = useMemo(() => buildOptions(rows, 'warehouse_name'), [rows]);
    const turnovers = useMemo(() => Array.from(new Set(
        rows.map((row) => row.turnover_grade_cluster || row.turnover_grade || '').filter(Boolean),
    )).sort(), [rows]);

    const setFilter = (key, value) => onChange({ ...filters, [key]: value });

    return (
        <div className="row-wrap fbo-filterbar" style={{ alignItems: 'end', gap: 8 }}>
            <label className="stack-4" style={{ minWidth: 220 }}>
                <span className="muted">Поиск</span>
                <input
                    type="search"
                    value={filters.query}
                    placeholder="Артикул, SKU, товар, склад"
                    onChange={(event) => setFilter('query', event.target.value)}
                />
            </label>
            <label className="stack-4" style={{ minWidth: 160 }}>
                <span className="muted">Кластер</span>
                <select value={filters.cluster} onChange={(event) => setFilter('cluster', event.target.value)}>
                    <option value="">Все</option>
                    {clusters.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
            </label>
            <label className="stack-4" style={{ minWidth: 180 }}>
                <span className="muted">Склад</span>
                <select value={filters.warehouse} onChange={(event) => setFilter('warehouse', event.target.value)}>
                    <option value="">Все</option>
                    {warehouses.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
            </label>
            <label className="stack-4" style={{ minWidth: 160 }}>
                <span className="muted">Ликвидность</span>
                <select value={filters.turnover} onChange={(event) => setFilter('turnover', event.target.value)}>
                    <option value="">Все</option>
                    {turnovers.map((value) => <option key={value} value={value}>{getTurnoverLabel(value)}</option>)}
                </select>
            </label>
            <label className="row-wrap" style={{ gap: 6, minHeight: 36 }}>
                <input
                    type="checkbox"
                    checked={filters.onlyWithStock}
                    onChange={(event) => setFilter('onlyWithStock', event.target.checked)}
                />
                <span>Только с остатком</span>
            </label>
            <button type="button" className="ghost" onClick={onReset}>Сбросить</button>
            <button type="button" onClick={onExport} disabled={exportDisabled}>Скачать CSV</button>
        </div>
    );
}

function LegacyInventoryTable({ rows, emptyText }) {
    return (
        <div className="analytics-table-wrapper">
            <table style={{ minWidth: 1280, tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={{ width: 130 }}>Артикул</th>
                        <th style={{ width: 110 }}>SKU</th>
                        <th style={{ width: 260 }}>Название</th>
                        <th style={{ width: 140 }}>Кластер</th>
                        <th style={{ width: 180 }}>Склад</th>
                        <th style={{ width: 90 }}>Доступно</th>
                        <th style={{ width: 90 }}>Готовим</th>
                        <th style={{ width: 95 }}>В заявках</th>
                        <th style={{ width: 80 }}>В пути</th>
                        <th style={{ width: 95 }} title="Среднесуточное количество проданных единиц за последние 28 дней">Прод./день</th>
                        <th style={{ width: 95 }} title="Количество дней, на которое хватит остатка с учетом среднесуточных продаж за 28 дней">Дней запаса</th>
                        <th style={{ width: 150 }}>Ликвидность</th>
                        <th style={{ width: 140 }}>Синхронизация</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={13} className="muted">{emptyText}</td>
                        </tr>
                    ) : rows.map((item, index) => {
                        const turnover = item.turnover_grade_cluster || item.turnover_grade || '';
                        return (
                            <tr key={item.id || `${item.sku}:${item.warehouse_id}:${item.cluster_id}:${index}`}>
                                <td>{item.offer_id || '-'}</td>
                                <td>{item.sku || '-'}</td>
                                <td style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>{item.name || '-'}</td>
                                <td>{item.cluster_name || item.cluster_id || '-'}</td>
                                <td>{item.warehouse_name || item.warehouse_id || '-'}</td>
                                <td>{formatMetric(item.available ?? item.available_stock_count, 0)}</td>
                                <td>{formatMetric(item.valid_stock_count, 0)}</td>
                                <td>{formatMetric(item.requested_stock_count, 0)}</td>
                                <td>{formatMetric(item.transit_stock_count, 0)}</td>
                                <td>{formatMetric(item.ads)}</td>
                                <td>{formatMetric(item.idc, 1)}</td>
                                <td title={turnover}>{getTurnoverLabel(turnover)}</td>
                                <td>{formatDateTime(item.synced_at)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function UnifiedInventoryTable({ rows, tableId, exportName }) {
    const tableData = useMemo(() => rows.map((item, index) => {
        const turnover = item.turnover_grade_cluster || item.turnover_grade || '';
        return {
            ...item,
            rowNum: index + 1,
            offer_id: item.offer_id || '-',
            sku: item.sku || '-',
            name: item.name || '-',
            cluster_name: item.cluster_name || item.cluster_id || '-',
            warehouse_name: item.warehouse_name || item.warehouse_id || '-',
            turnover_grade_display: getTurnoverLabel(turnover),
        };
    }), [rows]);

    return (
        <UnifiedTable
            tableId={tableId}
            columns={inventoryColumns}
            data={tableData}
            totalRows={tableData.length}
            exportFilename={exportName.replace(/\.csv$/i, '')}
            onExportCsv={true}
            onCopyTable={true}
            getRowKey={(row, index) => row.id || `${row.sku}:${row.warehouse_id}:${row.cluster_id}:${index}`}
        />
    );
}

function InventoryBlock({ title, rows, totalRows, filters, setFilters, emptyText, exportName, tableId }) {
    const filteredRows = useMemo(() => filterRows(rows, filters), [rows, filters]);

    return (
        <div className="stack-8">
            <strong>{title}</strong>
            <SummaryLine rows={filteredRows} totalRows={totalRows || rows.length} />
            <FilterBar
                rows={rows}
                filters={filters}
                onChange={setFilters}
                onReset={() => setFilters(emptyFilters)}
                onExport={() => downloadCsv(filteredRows, exportName)}
                exportDisabled={filteredRows.length === 0}
            />
            <UnifiedInventoryTable rows={filteredRows} emptyText={emptyText} tableId={tableId} exportName={exportName} />
        </div>
    );
}

function LegacyReplenishmentTable({ rows }) {
    return (
        <div className="analytics-table-wrapper">
            <table style={{ minWidth: 1380, tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={{ width: 140 }}>Артикул</th>
                        <th style={{ width: 110 }}>SKU</th>
                        <th style={{ width: 260 }}>Название</th>
                        <th style={{ width: 170 }}>Кластер</th>
                        <th style={{ width: 190 }}>Склад</th>
                        <th style={{ width: 90 }}>Доступно</th>
                        <th style={{ width: 90 }}>В движении</th>
                        <th style={{ width: 100 }}>Спрос/день</th>
                        <th style={{ width: 100 }}>Дней запаса</th>
                        <th style={{ width: 90 }}>Кратность</th>
                        <th style={{ width: 110 }}>Потребность</th>
                        <th style={{ width: 120 }}>Рекомендовано</th>
                        <th style={{ width: 220 }}>Причина</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={13} className="muted">Расчет еще не выполнялся или рекомендаций нет.</td>
                        </tr>
                    ) : rows.map((row, index) => (
                        <tr key={`${row.article}:${row.cluster_id}:${row.warehouse_id}:${index}`}>
                            <td>{row.article || '-'}</td>
                            <td>{row.sku || '-'}</td>
                            <td style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>{row.name || '-'}</td>
                            <td>{row.cluster_name || row.cluster_id || '-'}</td>
                            <td>{row.warehouse_name || row.warehouse_id || '-'}</td>
                            <td>{formatMetric(row.current_available, 0)}</td>
                            <td>{formatMetric(row.in_motion, 0)}</td>
                            <td>{formatMetric(row.avg_demand_per_day, 2)}</td>
                            <td>{formatMetric(row.stock_cover_days, 1)}</td>
                            <td>{row.box_multiple || '-'}</td>
                            <td>{formatMetric(row.raw_need, 1)}</td>
                            <td><strong>{formatMetric(row.recommended_units, 0)}</strong></td>
                            <td>{row.reason || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ReplenishmentTable({ rows, mode }) {
    const [filters, setFilters] = useState(emptyReplenishmentFilters);
    const [search, setSearch] = useState('');
    // В режиме «Артикул — Кластер» строка — это кластер целиком, склада у неё нет.
    const byWarehouse = mode === 'article_warehouse';
    const columns = useMemo(
        () => (byWarehouse ? replenishmentColumns : replenishmentColumns.filter((col) => col.key !== 'warehouse_name')),
        [byWarehouse],
    );

    const baseData = useMemo(() => rows.map((row) => ({
        ...row,
        article: row.article || '-',
        sku: row.sku || '-',
        name: row.name || '-',
        // Склад вне карты кластеров: в режиме кластеров показываем его в названии,
        // иначе не понять, куда везти.
        cluster_name: row.cluster_name
            || (byWarehouse ? 'Нет в карте кластеров' : `Нет в карте: ${row.warehouse_name || row.warehouse_id || '-'}`),
        warehouse_name: row.warehouse_name || row.warehouse_id || '-',
        reason: row.reason || '-',
        reason_group: reasonGroup(row.reason),
    })), [rows, byWarehouse]);

    const options = useMemo(() => {
        const uniq = (key) => Array.from(new Set(baseData.map((row) => row[key]).filter((value) => value && value !== '-')))
            .sort((a, b) => String(a).localeCompare(String(b), 'ru'));
        return { clusters: uniq('cluster_name'), warehouses: uniq('warehouse_name'), reasons: uniq('reason_group') };
    }, [baseData]);

    const tableData = useMemo(() => baseData
        .filter((row) => {
            if (filters.cluster && row.cluster_name !== filters.cluster) return false;
            if (byWarehouse && filters.warehouse && row.warehouse_name !== filters.warehouse) return false;
            if (filters.reason && row.reason_group !== filters.reason) return false;
            if (filters.onlyToShip && Number(row.recommended_units || 0) <= 0) return false;
            return true;
        })
        .map((row, index) => ({ ...row, rowNum: index + 1 })), [baseData, filters, byWarehouse]);

    const totals = useMemo(() => tableData.reduce((acc, row) => {
        const units = Number(row.recommended_units || 0);
        if (units > 0) {
            acc.positions += 1;
            acc.units += units;
            acc.articles.add(row.article);
        }
        if (Number(row.uncapped_units || 0) > units) acc.capped += 1;
        return acc;
    }, { positions: 0, units: 0, articles: new Set(), capped: 0 }), [tableData]);

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
    const hasFilters = filters.cluster || filters.warehouse || filters.reason || filters.onlyToShip || search;

    return (
        <div className="stack-8 fbo-replenishment__result">
            <div className="row-wrap fbo-replenishment__filters">
                <label className="fbo-filter">
                    <span className="muted">Кластер</span>
                    <select value={filters.cluster} onChange={(event) => setFilter('cluster', event.target.value)}>
                        <option value="">Все</option>
                        {options.clusters.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </label>
                {byWarehouse && (
                    <label className="fbo-filter">
                        <span className="muted">Склад</span>
                        <select value={filters.warehouse} onChange={(event) => setFilter('warehouse', event.target.value)}>
                            <option value="">Все</option>
                            {options.warehouses.map((value) => <option key={value} value={value}>{value}</option>)}
                        </select>
                    </label>
                )}
                <label className="fbo-filter fbo-filter--wide">
                    <span className="muted">Причина</span>
                    <select value={filters.reason} onChange={(event) => setFilter('reason', event.target.value)}>
                        <option value="">Все</option>
                        {options.reasons.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </label>
                <label className="fbo-replenishment__check">
                    <input
                        type="checkbox"
                        checked={filters.onlyToShip}
                        onChange={(event) => setFilter('onlyToShip', event.target.checked)}
                    />
                    <span>Только к отгрузке</span>
                </label>
                <button
                    type="button"
                    className="ghost"
                    disabled={!hasFilters}
                    onClick={() => { setFilters(emptyReplenishmentFilters); setSearch(''); }}
                >
                    Сбросить
                </button>
            </div>
            <div className="muted">
                Строк: {tableData.length}{tableData.length !== rows.length ? ` из ${rows.length}` : ''}
                {' '}· К отгрузке: {totals.units.toLocaleString('ru-RU')} шт в {totals.positions} позициях, артикулов {totals.articles.size}
                {totals.capped > 0 ? ` · урезано распределяемым остатком: ${totals.capped}` : ''}
            </div>
            <UnifiedTable
                tableId={byWarehouse ? 'ozon-fbo-replenishment-warehouse' : 'ozon-fbo-replenishment'}
                columns={columns}
                data={tableData}
                search={search}
                onSearchChange={setSearch}
                exportFilename={`ozon-fbo-replenishment-${todayIso()}`}
                exportFormat="xlsx"
                onExportCsv={true}
                onCopyTable={true}
                getRowKey={(row) => `${row.article}:${row.cluster_name}:${row.warehouse_id}`}
            />
        </div>
    );
}

function ReplenishmentHelp() {
    return (
        <details className="fbo-help">
            <summary>Как считается подсорт и что значат поля</summary>
            <div className="fbo-help__body">
                <p>
                    <strong>Что делает.</strong> Считает, сколько штук каждого артикула довезти на склады OZON (FBO),
                    чтобы товара хватило на выбранное число дней вперёд.
                </p>
                <p><strong>Как считает, по шагам:</strong></p>
                <ol>
                    <li>Берём ежедневные снимки остатков FBO за период «с — по». Если за день остаток на складе уменьшился — это продажи. Если вырос — это поставка, она в продажи не идёт.</li>
                    <li><strong>Спрос в день</strong> = продано за период ÷ дни, когда товар был в наличии. Дни с нулём на складе не считаем: в них продать было нечего, иначе спрос занижается.</li>
                    <li><strong>Потребность</strong> = спрос в день × горизонт − доступно сейчас − в движении (готовится к продаже, в заявках, в пути).</li>
                    <li><strong>Рекомендовано</strong> = потребность, округлённая арифметически до штук или целых коробов: вверх, только если дробная часть больше 0,5. Если задан распределяемый остаток, сумма по артикулу на все кластеры не превысит его.</li>
                </ol>
                <p><strong>Поля:</strong></p>
                <ul>
                    <li><strong>Режим.</strong> «Артикул — Кластер»: одна строка на артикул в кластере, остатки всех складов кластера складываются — для поставки на кластер. «Артикул — Склад»: строка на каждый конкретный склад.</li>
                    <li><strong>Период с / по</strong> — прошлое: за какие дни смотреть историю, из неё считается спрос. Длиннее период — спокойнее цифры, короче — быстрее реагирует на всплеск. Обычно 28 дней. История копится раз в сутки с момента подключения кабинета, раньше неё данных нет.</li>
                    <li><strong>Горизонт подсорта</strong> — будущее: на сколько дней должно хватить товара. Период отвечает «как быстро продаётся», горизонт — «на сколько везём». Ставьте дни до следующей поставки плюс дни в пути до склада.</li>
                    <li><strong>Минимум дней наблюдения</strong> — защита от завышенного спроса у новых товаров и коротких историй. Если товар был в наличии меньше этого числа дней, продажи делятся на минимум. Пример: 6 шт за 2 дня при минимуме 7 → 6 ÷ 7 ≈ 0,9 в день, а не 3. Не ставьте больше длины периода.</li>
                    <li><strong>Кратность коробов</strong> — сколько штук в коробе: рекомендация округляется до целых коробов (вверх — если не хватает меньше половины короба). Пусто — без округления.</li>
                    <li><strong>Распределяемый остаток</strong> — сколько штук артикула есть у вас для отгрузки. Это потолок: больше него по артикулу не рекомендуем, нехватку делим между кластерами пропорционально потребности, целыми коробами. Пусто — без ограничения.</li>
                    <li><strong>Доли регионов</strong> — доля спроса артикула, которая приходится на кластер, в процентах. Дни, когда в кластере был остаток, считаем по его собственным продажам. В дни без остатка его покупатели брали товар с других складов — эту долю спроса переносим в кластер и ровно столько же снимаем с ближайшего кластера, где в тот день был остаток. Общий спрос артикула не меняется, меняется только раскладка по регионам. Пример: спрос 10 шт/день, Казань 30 %, в Казани товара не было — Казани 3 шт/день, у соседнего кластера на 3 меньше. Сумма долей — не больше 100 %, иначе расчёт не запустится; можно меньше, если заданы не все кластеры. «Пересчитать доли» заполняет целые проценты по истории продаж (в сумме 100 %) — это то, откуда товар уезжал, а не где покупатели.</li>
                </ul>
            </div>
        </details>
    );
}

function CollapsibleSection({ title, hint, open, onToggle, actions, children }) {
    return (
        <div className={`panel__section fbo-collapsible${open ? ' fbo-collapsible--open' : ''}`}>
            <div className="panel__head">
                <button type="button" className="fbo-collapsible__toggle" onClick={onToggle} aria-expanded={open}>
                    <span className="fbo-collapsible__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
                    <span className="fbo-collapsible__title">
                        <strong>{title}</strong>
                        {hint && <span className="muted">{hint}</span>}
                    </span>
                </button>
                {open && <div className="row-wrap">{actions}</div>}
            </div>
            {open && children}
        </div>
    );
}

function OzonFboPanel({ accountId }) {
    const { request, showStatus } = useApi();
    // Подвкладка переживает F5, как и в FBS.
    const [activeMode, setActiveMode] = usePersistedState(`ozon-fbo-mode-${accountId || 'none'}`, 'live');
    const [historyBusy, setHistoryBusy] = useState(false);
    const [liveBusy, setLiveBusy] = useState(false);
    const [factDate, setFactDate] = useState(todayIso());
    const [historyRows, setHistoryRows] = useState([]);
    const [historyMeta, setHistoryMeta] = useState({ total: 0, page: 1, page_size: 100 });
    const [liveRows, setLiveRows] = useState([]);
    const [historyFilters, setHistoryFilters] = useState(emptyFilters);
    const [liveFilters, setLiveFilters] = useState(emptyFilters);
    const [replenishmentBusy, setReplenishmentBusy] = useState(false);
    const [replenishmentRows, setReplenishmentRows] = useState([]);
    const [replenishmentMeta, setReplenishmentMeta] = useState(null);
    const [replenishmentParams, setReplenishmentParams] = useState({
        mode: 'article_cluster',
        period_from: daysAgoIso(27),
        period_to: todayIso(),
        horizon_days: '21',
        min_obs_days_for_rate: '7',
    });
    const [boxRulesOpen, setBoxRulesOpen] = usePersistedState('ozon-fbo-box-rules-open', false);
    const [clustersOpen, setClustersOpen] = usePersistedState('ozon-fbo-clusters-open', false);
    const [boxRules, setBoxRules] = useState([]);
    const [clusterDistribution, setClusterDistribution] = useState([]);
    // Сколько ушло из каждого кластера за период — то, из чего считаются доли.
    // Показываем рядом с долей, в настройки не сохраняем.
    const [shareStats, setShareStats] = useState(null);
    // Настройки подсорта живут в БД на кабинет: их набивают руками, и терять их
    // при F5 или переходе в другой раздел нельзя.
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const boxImportRef = useRef(null);
    const clusterImportRef = useRef(null);
    const isTodaySelected = factDate === todayIso();

    useEffect(() => {
        if (!accountId) return undefined;
        let cancelled = false;
        setSettingsLoaded(false);
        request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/settings`)
            .then((data) => {
                if (cancelled) return;
                setBoxRules(Array.isArray(data?.box_rules) ? data.box_rules.map((row) => ({
                    article: row.article || '',
                    multiple: row.multiple == null ? '' : String(row.multiple),
                    distributable: row.distributable == null ? '' : String(row.distributable),
                })) : []);
                setClusterDistribution(Array.isArray(data?.cluster_distribution) ? data.cluster_distribution.map((row) => ({
                    cluster_name: row.cluster_name || '',
                    share: row.share == null ? '' : String(row.share),
                })) : []);
                if (data?.params && typeof data.params === 'object') {
                    // Даты периода не восстанавливаем: сохранённые раз «с — по» через
                    // неделю считали бы по устаревшему окну. Период всегда скользящий.
                    const saved = { ...data.params };
                    delete saved.period_from;
                    delete saved.period_to;
                    setReplenishmentParams((prev) => ({ ...prev, ...saved }));
                }
            })
            .then(() => { if (!cancelled) setSettingsLoaded(true); })
            .catch((error) => {
                // Пустые настройки сервер отдаёт как пустые списки. Ошибка — это не
                // «настроек нет»: автосохранение не включаем, иначе оно затрёт
                // сохранённые кратности и доли пустыми.
                if (!cancelled) {
                    showStatus('error', 'Не удалось загрузить настройки подсорта — обновите страницу. '
                        + 'Пока не загрузятся, правки не сохраняются. ' + (error?.message || ''));
                }
            });
        return () => { cancelled = true; };
    }, [accountId, request, showStatus]);

    // Автосохранение с задержкой: печатают построчно, на каждый символ в БД не ходим.
    useEffect(() => {
        if (!accountId || !settingsLoaded) return undefined;
        const timer = setTimeout(() => {
            const payload = {
                box_rules: boxRules
                    .filter((row) => String(row.article || '').trim())
                    .map((row) => ({
                        article: String(row.article).trim(),
                        // API принимает кратность от 1; мусор и нули не отправляем,
                        // иначе 422 и настройки молча не сохранятся.
                        multiple: Number(row.multiple) >= 1 ? Math.trunc(Number(row.multiple)) : null,
                        distributable: distributableValue(row.distributable),
                    })),
                cluster_distribution: clusterDistribution
                    .filter((row) => String(row.cluster_name || '').trim())
                    .map((row) => ({
                        cluster_name: String(row.cluster_name).trim(),
                        share: shareValue(row.share),
                    })),
                params: {
                    mode: replenishmentParams.mode,
                    horizon_days: replenishmentParams.horizon_days,
                    min_obs_days_for_rate: replenishmentParams.min_obs_days_for_rate,
                },
            };
            request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/settings`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            }).catch((error) => {
                // Следующая правка повторит сохранение, но пользователь должен знать,
                // что сейчас настройки не записались.
                showStatus('error', 'Настройки подсорта не сохранились: ' + (error?.message || 'ошибка сервера'));
            });
        }, 800);
        return () => clearTimeout(timer);
    }, [accountId, settingsLoaded, boxRules, clusterDistribution, replenishmentParams, request, showStatus]);

    const loadHistory = useCallback(async (dateToLoad = factDate) => {
        const query = new URLSearchParams({
            page: '1',
            page_size: '5000',
            fact_date: dateToLoad,
        });
        const data = await request(`/api/v1/ozon-ops/${accountId}/fbo/inventory/daily?${query.toString()}`);
        setHistoryRows(Array.isArray(data?.items) ? data.items : []);
        setHistoryMeta({
            total: Number(data?.total || 0),
            page: Number(data?.page || 1),
            page_size: Number(data?.page_size || 5000),
        });
    }, [accountId, factDate, request]);

    useEffect(() => {
        if (!accountId || activeMode !== 'history') return;
        loadHistory().catch(() => {
            showStatus('error', 'Не удалось загрузить историю FBO.');
        });
    }, [accountId, activeMode, loadHistory, showStatus]);

    const syncHistory = useCallback(async () => {
        if (!isTodaySelected) {
            showStatus('warning', 'Синхронизация доступна только за сегодня. Прошлые даты можно только просматривать.');
            return;
        }
        setHistoryBusy(true);
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbo/inventory/sync`, { method: 'POST' });
            await loadHistory();
            if (data?.status === 'partial') {
                showStatus('warning', 'История FBO обновлена частично: часть SKU OZON не отдал.');
            } else {
                showStatus('success', 'История FBO обновлена.');
            }
        } catch (error) {
            showStatus('error', error?.message || 'Синхронизация истории FBO завершилась с ошибкой.');
        } finally {
            setHistoryBusy(false);
        }
    }, [accountId, isTodaySelected, loadHistory, request, showStatus]);

    const loadLive = useCallback(async () => {
        setLiveBusy(true);
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbo/inventory/live`, { method: 'POST' });
            setLiveRows(Array.isArray(data?.items) ? data.items : []);
            if (data?.status === 'partial') {
                showStatus('warning', data?.errors?.[0] || 'Текущие остатки FBO загружены частично: часть SKU OZON не отдал.');
            } else {
                showStatus('success', 'Текущие остатки FBO загружены.');
            }
        } catch (error) {
            showStatus('error', error?.message || 'Не удалось загрузить текущие остатки FBO.');
        } finally {
            setLiveBusy(false);
        }
    }, [accountId, request, showStatus]);

    const loadArticlesForBoxRules = useCallback(async () => {
        if (!accountId) return;
        try {
            let offset = 0;
            const limit = 500;
            let totalRows = 0;
            const all = [];
            do {
                const query = new URLSearchParams({
                    limit: String(limit),
                    offset: String(offset),
                    search: '',
                    sort_by: '',
                    sort_dir: 'asc',
                });
                const data = await request(`/api/v1/ozon/${accountId}/assortment?${query.toString()}`);
                const rows = data?.rows || [];
                totalRows = Number(data?.total || rows.length);
                all.push(...rows);
                offset += rows.length;
                if (!rows.length) break;
            } while (offset < totalRows);

            const prev = new Map(boxRules.map((row) => [row.article, row]));
            const uniq = new Map();
            all.forEach((row) => {
                const article = String(row.offer_id || '').trim();
                if (!article || uniq.has(article)) return;
                uniq.set(article, { article, multiple: '', distributable: '', ...(prev.get(article) || {}) });
            });
            setBoxRules(Array.from(uniq.values()).sort((a, b) => a.article.localeCompare(b.article, 'ru')));
            showStatus('success', `Загружено артикулов: ${uniq.size}`);
        } catch (error) {
            showStatus('error', error?.message || 'Не удалось загрузить ассортимент для кратности.');
        }
    }, [accountId, boxRules, request, showStatus]);

    // Доли берём из собственной истории FBO: дневные просадки остатка по кластеру —
    // тот же прокси спроса, на котором стоит расчёт подсорта. Ноль вызовов в OZON.
    const loadClustersForDistribution = useCallback(async ({ silent = false, keepFilled = true } = {}) => {
        if (!accountId) return;
        try {
            const query = new URLSearchParams();
            if (replenishmentParams.period_from) query.set('period_from', replenishmentParams.period_from);
            if (replenishmentParams.period_to) query.set('period_to', replenishmentParams.period_to);
            const suffix = query.toString() ? `?${query.toString()}` : '';
            const [options, suggested] = await Promise.all([
                request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/clusters`),
                request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/clusters/suggested-shares${suffix}`)
                    .catch(() => ({ items: [] })),
            ]);
            const shareByCluster = new Map(
                (suggested?.items || []).map((item) => [item.cluster_name, item.share])
            );
            setShareStats(toShareStats(suggested));
            setClusterDistribution((prev) => {
                const prevByName = new Map(prev.map((row) => [row.cluster_name, row]));
                // Расчётные доли — единый комплект на 100 %. Если в таблице уже есть
                // ручные доли, подмешивать к ним расчётные нельзя: сумма уйдёт за 100 %.
                // Тогда «Загрузить кластеры» только добавляет недостающие кластеры пустыми.
                const hasFilled = prev.some((row) => shareValue(row.share) !== null);
                const useSuggested = !keepFilled || !hasFilled;
                const rows = (Array.isArray(options) ? options : [])
                    .map((item) => String(item.cluster_name || '').trim())
                    .filter(Boolean)
                    .map((cluster_name) => {
                        const existing = prevByName.get(cluster_name);
                        if (!useSuggested) {
                            return existing || { cluster_name, share: '' };
                        }
                        const suggestedShare = shareByCluster.get(cluster_name);
                        return {
                            cluster_name,
                            share: suggestedShare == null ? '' : String(suggestedShare),
                        };
                    });
                return rows;
            });
            if (!silent) {
                showStatus(
                    'success',
                    shareByCluster.size
                        ? `Кластеры загружены, доли рассчитаны по истории FBO: ${shareByCluster.size}`
                        : 'Кластеры загружены. Истории FBO пока не хватает для расчёта долей.'
                );
            }
        } catch (error) {
            if (!silent) showStatus('error', error?.message || 'Не удалось загрузить кластеры.');
        }
    }, [accountId, replenishmentParams.period_from, replenishmentParams.period_to, request, showStatus]);

    // Цифры «продано за период» нужны и когда доли уже заполнены: подтягиваем при
    // открытии вкладки и смене периода. Только чтение истории из БД, без OZON.
    useEffect(() => {
        if (!accountId || activeMode !== 'replenishment') return undefined;
        let cancelled = false;
        const query = new URLSearchParams();
        if (replenishmentParams.period_from) query.set('period_from', replenishmentParams.period_from);
        if (replenishmentParams.period_to) query.set('period_to', replenishmentParams.period_to);
        const suffix = query.toString() ? `?${query.toString()}` : '';
        request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/clusters/suggested-shares${suffix}`)
            .then((data) => { if (!cancelled) setShareStats(toShareStats(data)); })
            .catch(() => { if (!cancelled) setShareStats(null); });
        return () => { cancelled = true; };
    }, [accountId, activeMode, replenishmentParams.period_from, replenishmentParams.period_to, request]);

    // Первое открытие вкладки с пустой таблицей — заполняем сами, без нажатий.
    const clustersAutoloadedRef = useRef(false);
    useEffect(() => {
        clustersAutoloadedRef.current = false;
    }, [accountId]);
    useEffect(() => {
        if (activeMode !== 'replenishment') return;
        if (!settingsLoaded || clustersAutoloadedRef.current) return;
        if (clusterDistribution.length > 0) {
            clustersAutoloadedRef.current = true;
            return;
        }
        clustersAutoloadedRef.current = true;
        loadClustersForDistribution({ silent: true });
    }, [activeMode, settingsLoaded, clusterDistribution.length, loadClustersForDistribution]);

    const importBoxRules = async (file) => {
        try {
            const rows = (await readSettingsSheet(file, boxRuleColumns)).filter((row) => row.article);
            setBoxRules(rows);
            showStatus('success', `Загружено строк кратности: ${rows.length}`);
        } catch (error) {
            showStatus('error', 'Не удалось прочитать Excel. Нужны колонки «Артикул», «Кратность», «Распределяемый остаток».');
        }
    };

    const importClusterDistribution = async (file) => {
        try {
            // Файл только проставляет доли кластерам OZON из таблицы: незнакомое
            // название — опечатка или чужой кластер, его доля нигде бы не учлась.
            const rows = (await readSettingsSheet(file, clusterShareColumns)).filter((row) => row.cluster_name);
            const shareByName = new Map(rows.map((row) => [row.cluster_name, row.share]));
            const known = new Set(clusterDistribution.map((row) => row.cluster_name));
            const unknown = rows.map((row) => row.cluster_name).filter((name) => !known.has(name));
            setClusterDistribution((prev) => prev.map((row) => ({
                ...row,
                share: shareByName.has(row.cluster_name) ? shareByName.get(row.cluster_name) : '',
            })));
            const applied = rows.length - unknown.length;
            if (unknown.length) {
                showStatus('warning', `Доли загружены: ${applied}. Не найдены кластеры: ${unknown.join(', ')}`);
            } else {
                showStatus('success', `Доли загружены: ${applied}`);
            }
        } catch (error) {
            showStatus('error', 'Не удалось прочитать Excel. Нужны колонки «Кластер» и «Доля, %».');
        }
    };

    // Доли — части одного спроса: больше 100 % в сумме быть не может.
    const sharesTotal = useMemo(
        () => clusterDistribution.reduce((sum, row) => sum + (shareValue(row.share) || 0), 0),
        [clusterDistribution],
    );
    const sharesOverflow = sharesTotal > 100.0001;

    const calculateReplenishment = useCallback(async () => {
        setReplenishmentBusy(true);
        try {
            const payload = {
                mode: replenishmentParams.mode,
                period_from: replenishmentParams.period_from || null,
                period_to: replenishmentParams.period_to || null,
                horizon_days: Number(replenishmentParams.horizon_days || 21),
                min_obs_days_for_rate: Number(replenishmentParams.min_obs_days_for_rate || 7),
                box_rules: boxRules.filter((row) => String(row.article || '').trim()).map((row) => ({
                    article: String(row.article).trim(),
                    multiple: Number(row.multiple) >= 1 ? Math.trunc(Number(row.multiple)) : null,
                    distributable: distributableValue(row.distributable),
                })),
                cluster_distribution: clusterDistribution.filter((row) => row.cluster_name).map((row) => ({
                    cluster_name: row.cluster_name,
                    share: shareValue(row.share),
                })),
            };
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbo/replenishment/calculate`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setReplenishmentRows(Array.isArray(data?.items) ? data.items : []);
            setReplenishmentMeta(data || null);
            showStatus('success', 'Подсорт рассчитан.');
        } catch (error) {
            showStatus('error', error?.message || 'Не удалось рассчитать подсорт.');
        } finally {
            setReplenishmentBusy(false);
        }
    }, [accountId, boxRules, clusterDistribution, replenishmentParams, request, showStatus]);

    return (
        <div className="stack-12" style={{ marginTop: 16 }}>
            <div className="account-tabs account-tabs--tertiary">
                <button
                    type="button"
                    className={activeMode === 'live' ? 'active' : ''}
                    onClick={() => setActiveMode('live')}
                >
                    Остатки сейчас
                </button>
                <button
                    type="button"
                    className={activeMode === 'history' ? 'active' : ''}
                    onClick={() => setActiveMode('history')}
                >
                    История
                </button>
                <button
                    type="button"
                    className={activeMode === 'replenishment' ? 'active' : ''}
                    onClick={() => setActiveMode('replenishment')}
                >
                    Подсорт
                </button>
            </div>

            {activeMode === 'live' && (
                <>
                    <div className="stack-8">
                        <strong>Остатки сейчас</strong>
                        <div className="muted">Live-запрос в OZON API без записи в дневную историю.</div>
                        <SummaryLine rows={liveRows} />
                        <div className="row-wrap">
                            <button type="button" className="primary" onClick={loadLive} disabled={liveBusy}>
                                Запросить сейчас
                            </button>
                        </div>
                    </div>

                    <InventoryBlock
                        title="Остатки сейчас"
                        rows={liveRows}
                        filters={liveFilters}
                        setFilters={setLiveFilters}
                        emptyText="Live-запрос еще не выполнялся."
                        exportName={`ozon-fbo-live-${todayIso()}.csv`}
                        tableId="ozon-fbo-live"
                    />
                </>
            )}

            {activeMode === 'history' && (
                <>
                    <div className="stack-8">
                        <strong>История FBO</strong>
                        <div className="muted">Ежедневный факт по паре артикул-склад из OZON API.</div>
                        <label className="stack-4" style={{ maxWidth: 220 }}>
                            <span className="muted">Дата снимка</span>
                            <input
                                type="date"
                                value={factDate}
                                onChange={(event) => setFactDate(event.target.value || todayIso())}
                            />
                        </label>
                        <div className="muted">Всего строк за дату: {historyMeta.total}</div>
                        {!isTodaySelected && (
                            <div className="muted">Для выбранной даты доступен только просмотр сохраненного снимка.</div>
                        )}
                        <div className="row-wrap">
                            <button
                                type="button"
                                onClick={syncHistory}
                                disabled={historyBusy || !isTodaySelected}
                                title={!isTodaySelected ? 'OZON API отдает только текущий снимок; прошлые даты не синхронизируем вручную.' : undefined}
                            >
                                Синхронизировать сегодня
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setHistoryBusy(true);
                                    loadHistory()
                                        .then(() => showStatus('success', 'История FBO обновлена на экране.'))
                                        .catch((error) => showStatus('error', error?.message || 'Не удалось обновить историю FBO.'))
                                        .finally(() => setHistoryBusy(false));
                                }}
                                disabled={historyBusy}
                            >
                                Обновить историю
                            </button>
                        </div>
                    </div>

                    <InventoryBlock
                        title="История за выбранную дату"
                        rows={historyRows}
                        totalRows={historyMeta.total}
                        filters={historyFilters}
                        setFilters={setHistoryFilters}
                        emptyText="Нет сохраненных дневных остатков FBO за выбранную дату."
                        exportName={`ozon-fbo-history-${factDate}.csv`}
                        tableId="ozon-fbo-history"
                    />
                </>
            )}

            {activeMode === 'replenishment' && (
                <div className="stack-12 fbo-replenishment">
                    <div className="stack-8">
                        <strong>Подсорт</strong>
                        <div className="muted">Сколько штук довезти на склады OZON, чтобы товара хватило на горизонт. Спрос считаем по ежедневной истории остатков FBO.</div>
                        <ReplenishmentHelp />
                        <div className="panel__grid">
                            <label title="Кластер — одна строка на артикул в кластере; Склад — строка на каждый склад">
                                Режим
                                <select
                                    value={replenishmentParams.mode}
                                    onChange={(event) => setReplenishmentParams((prev) => ({ ...prev, mode: event.target.value }))}
                                >
                                    <option value="article_cluster">Артикул - Кластер</option>
                                    <option value="article_warehouse">Артикул - Склад</option>
                                </select>
                            </label>
                            <label title="Начало истории, по которой считается спрос">
                                Период с
                                <input
                                    type="date"
                                    value={replenishmentParams.period_from}
                                    onChange={(event) => setReplenishmentParams((prev) => ({ ...prev, period_from: event.target.value }))}
                                />
                            </label>
                            <label title="Конец истории; текущий остаток берём на эту дату (или последний снимок до неё)">
                                Период по
                                <input
                                    type="date"
                                    value={replenishmentParams.period_to}
                                    onChange={(event) => setReplenishmentParams((prev) => ({ ...prev, period_to: event.target.value }))}
                                />
                            </label>
                            <label title="На сколько дней вперёд должно хватить товара: дни до следующей поставки + дни в пути">
                                Горизонт подсорта, дней
                                <input
                                    type="number"
                                    min="1"
                                    value={replenishmentParams.horizon_days}
                                    onChange={(event) => setReplenishmentParams((prev) => ({ ...prev, horizon_days: event.target.value }))}
                                />
                            </label>
                            <label title="Если товар был в наличии меньше этого числа дней, продажи делим на это число — чтобы короткая история не завышала спрос">
                                Минимум дней наблюдения
                                <input
                                    type="number"
                                    min="1"
                                    value={replenishmentParams.min_obs_days_for_rate}
                                    onChange={(event) => setReplenishmentParams((prev) => ({ ...prev, min_obs_days_for_rate: event.target.value }))}
                                />
                            </label>
                        </div>
                    </div>

                    <CollapsibleSection
                        title="Кратность коробов и распределяемый остаток"
                        hint={`Заполнено: кратность ${boxRules.filter((row) => toOptionalNumber(row.multiple)).length}, остаток ${boxRules.filter((row) => toOptionalNumber(row.distributable) !== null).length} из ${boxRules.length}`}
                        open={boxRulesOpen}
                        onToggle={() => setBoxRulesOpen((prev) => !prev)}
                        actions={(
                            <>
                                <button type="button" className="ghost" onClick={loadArticlesForBoxRules}>Загрузить ассортимент</button>
                                <button type="button" className="ghost" onClick={() => setBoxRules((prev) => [...prev, { article: '', multiple: '', distributable: '' }])}>Добавить строку</button>
                                <button
                                    type="button"
                                    className="ghost"
                                    onClick={() => downloadSettingsSheet(boxRules, boxRuleColumns, ['multiple', 'distributable'], 'Кратность коробов', 'fbo-kratnost-korobov.xlsx')}
                                >
                                    Скачать Excel
                                </button>
                                <button type="button" className="ghost" onClick={() => boxImportRef.current?.click()}>Загрузить Excel</button>
                                <input
                                    ref={boxImportRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        event.target.value = '';
                                        if (file) importBoxRules(file);
                                    }}
                                />
                            </>
                        )}
                    >
                        <div className="muted fbo-collapsible__note">
                            Кратность — штук в коробе, рекомендация округляется до целых коробов. Распределяемый остаток — максимум, который можно отгрузить по артикулу на все кластеры; выше него не рекомендуем. Пусто — без ограничения.
                        </div>
                        <div className="analytics-table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Артикул</th>
                                        <th>Кратность</th>
                                        <th>Распределяемый остаток</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {boxRules.length === 0 ? (
                                        <tr><td colSpan={4} className="muted">Кратность не заполнена. Нажмите «Загрузить ассортимент» или «Загрузить Excel».</td></tr>
                                    ) : boxRules.map((row, index) => (
                                        <tr key={index}>
                                            <td>
                                                <input
                                                    value={row.article}
                                                    onChange={(event) => setBoxRules((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, article: event.target.value } : item))}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={row.multiple}
                                                    onChange={(event) => setBoxRules((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, multiple: event.target.value } : item))}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={row.distributable ?? ''}
                                                    placeholder="без лимита"
                                                    onChange={(event) => setBoxRules((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, distributable: event.target.value } : item))}
                                                />
                                            </td>
                                            <td>
                                                <button type="button" className="ghost" onClick={() => setBoxRules((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}>Удалить</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Доли регионов"
                        hint={`Кластеров с долей: ${clusterDistribution.filter((row) => toOptionalNumber(row.share)).length} из ${clusterDistribution.length} · итого ${formatMetric(sharesTotal, 1)} %${sharesOverflow ? ' — больше 100 %!' : ''}`}
                        open={clustersOpen}
                        onToggle={() => setClustersOpen((prev) => !prev)}
                        actions={(
                            <>
                                <button type="button" className="ghost" onClick={() => loadClustersForDistribution()}>Загрузить кластеры</button>
                                <button type="button" className="ghost" onClick={() => loadClustersForDistribution({ keepFilled: false })} title="Перезаписать все доли расчётом по истории FBO за выбранный период">Пересчитать доли</button>
                                <button
                                    type="button"
                                    className="ghost"
                                    onClick={() => downloadSettingsSheet(clusterDistribution, clusterShareColumns, ['share'], 'Доли регионов', 'fbo-doli-regionov.xlsx')}
                                >
                                    Скачать Excel
                                </button>
                                <button type="button" className="ghost" onClick={() => clusterImportRef.current?.click()}>Загрузить Excel</button>
                                <input
                                    ref={clusterImportRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        event.target.value = '';
                                        if (file) importClusterDistribution(file);
                                    }}
                                />
                            </>
                        )}
                    >
                        <div className="muted fbo-collapsible__note">
                            Доля спроса артикула на кластер, %. В дни без остатка в кластере эта доля переносится к нему с ближайшего кластера, где товар был; общий спрос не растёт. Названия кластеров — как в OZON.
                            {shareStats?.periodFrom && (
                                <> «Продано за период» — сколько штук ушло со складов кластера с {shareStats.periodFrom} по {shareStats.periodTo}; это то, откуда товар уезжал, а не где покупатели.</>
                            )}
                        </div>
                        <div className="analytics-table-wrapper">
                            <table className="fbo-shares-table">
                                <thead>
                                    <tr>
                                        <th>Кластер</th>
                                        <th>Доля, %</th>
                                        <th title="Сколько штук ушло из кластера за период по истории FBO — из этого считает «Пересчитать доли»">
                                            Продано за период
                                        </th>
                                    </tr>
                                </thead>
                                <tfoot>
                                    <tr className={sharesOverflow ? 'fbo-shares-total fbo-shares-total--over' : 'fbo-shares-total'}>
                                        <td>Итого</td>
                                        <td>{formatMetric(sharesTotal, 1)} %{sharesOverflow ? ' — больше 100 %' : ''}</td>
                                        <td>{shareStats ? `${shareStats.total.toLocaleString('ru-RU')} шт` : ''}</td>
                                    </tr>
                                </tfoot>
                                <tbody>
                                    {clusterDistribution.length === 0 ? (
                                        <tr><td colSpan={3} className="muted">Кластеров нет. Нажмите «Загрузить кластеры».</td></tr>
                                    ) : clusterDistribution.map((row, index) => (
                                        <tr key={row.cluster_name || index}>
                                            <td>{row.cluster_name}</td>
                                            <td>
                                                <span className="fbo-share-input">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="1"
                                                        value={row.share}
                                                        onChange={(event) => setClusterDistribution((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, share: event.target.value } : item))}
                                                    />
                                                    <span>%</span>
                                                </span>
                                            </td>
                                            <td className="fbo-share-units">
                                                {shareStats
                                                    ? formatShareUnits(shareStats, row.cluster_name)
                                                    : '…'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CollapsibleSection>

                    <div className="row-wrap">
                        <button
                            type="button"
                            className="primary"
                            onClick={calculateReplenishment}
                            disabled={replenishmentBusy || sharesOverflow}
                            title={sharesOverflow ? `Сумма долей регионов ${formatMetric(sharesTotal, 1)} % — уменьшите до 100 %` : undefined}
                        >
                            {replenishmentBusy ? 'Считаю…' : 'Рассчитать подсорт'}
                        </button>
                        {sharesOverflow && (
                            <span className="fbo-shares-warning">
                                Сумма долей регионов {formatMetric(sharesTotal, 1)} % — больше 100 %. Уменьшите доли, чтобы рассчитать.
                            </span>
                        )}
                        <button
                            type="button"
                            className="ghost"
                            onClick={() => downloadReplenishmentWorkbook({
                                rows: replenishmentRows,
                                boxRules,
                                clusterDistribution,
                                params: replenishmentParams,
                                meta: replenishmentMeta,
                                filename: `ozon-fbo-replenishment-${todayIso()}.xlsx`,
                            })}
                            disabled={replenishmentRows.length === 0}
                            title="Excel с листами: Подсорт, По кластерам, По артикулам, Кратность коробов, Доли регионов, Параметры"
                        >
                            Итоговая таблица (Excel)
                        </button>
                        {replenishmentMeta && (
                            <span className="muted">
                                Период: {replenishmentMeta.period_from || '-'} — {replenishmentMeta.period_to || '-'}.
                            </span>
                        )}
                    </div>

                    {replenishmentMeta?.unmapped_warehouses?.length > 0 && (
                        <div className="fbo-replenishment__warning">
                            Склады без кластера в карте OZON: {replenishmentMeta.unmapped_warehouses.join(', ')}.
                            Их строки показаны с конкретным складом вместо кластера. Обновите карту кластеров, чтобы они встали в свой кластер.
                        </div>
                    )}

                    {replenishmentMeta && <ReplenishmentTable rows={replenishmentRows} mode={replenishmentMeta.mode} />}
                </div>
            )}
        </div>
    );
}

export default OzonFboPanel;
